import { useState, useEffect, useRef } from 'react';

interface LoadingState {
  post: boolean;
  adjacentPosts: boolean;
  relatedPosts: boolean;
}

export function useLoadingCoordinator() {
  const [loadingStates, setLoadingStates] = useState<LoadingState>({
    post: false,
    adjacentPosts: false,
    relatedPosts: false
  });
  
  const [isNavigating, setIsNavigating] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout>();
  const fallbackTimeoutRef = useRef<NodeJS.Timeout>();

  const updateLoadingState = (key: keyof LoadingState, value: boolean) => {
    setLoadingStates(prev => ({ ...prev, [key]: value }));
  };

  const startNavigation = () => {
    setIsNavigating(true);
    // Only mark critical resources as loading (post + adjacentPosts)
    setLoadingStates({
      post: true,
      adjacentPosts: true,
      relatedPosts: false
    });
    
    // Fallback timeout to prevent infinite loading (10 seconds)
    if (fallbackTimeoutRef.current) {
      clearTimeout(fallbackTimeoutRef.current);
    }
    fallbackTimeoutRef.current = setTimeout(() => {
      console.warn('Loading coordinator timeout - forcing completion');
      setLoadingStates({
        post: false,
        adjacentPosts: false,
        relatedPosts: false
      });
      setIsNavigating(false);
    }, 10000);
  };

  // Critical loading: post + adjacent posts only
  const isLoading = loadingStates.post || loadingStates.adjacentPosts;

  useEffect(() => {
    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // If critical resources finished loading, clear navigation state after a brief delay
    if (!isLoading && isNavigating) {
      // Clear fallback timeout since loading completed normally
      if (fallbackTimeoutRef.current) {
        clearTimeout(fallbackTimeoutRef.current);
      }
      
      timeoutRef.current = setTimeout(() => {
        setIsNavigating(false);
      }, 150); // Small delay to prevent flicker
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (fallbackTimeoutRef.current) {
        clearTimeout(fallbackTimeoutRef.current);
      }
    };
  }, [isLoading, isNavigating]);

  return {
    updateLoadingState,
    startNavigation,
    isLoading,
    isNavigating: isNavigating || isLoading,
    loadingStates
  };
}